import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from './api';
import { useAuth } from './auth';

export interface Workspace {
  publicId: string;
  name: string;
  defaultTz: string;
  brandColor: string | null;
  logoUrl: string | null;
  role: 'OWNER' | 'ADMIN' | 'MANAGER' | 'EDITOR' | 'APPROVER' | 'VIEWER';
  approvalRequired: boolean;
  /** Workspace may use pay-per-use platforms (X) regardless of the user's email allowlist. */
  payPerUseAllowed?: boolean;
}

interface WorkspaceStore {
  current: Workspace | null;
  _hydrated: boolean;
  setCurrent(w: Workspace | null): void;
}

/**
 * Zustand store with localStorage persistence.
 *
 * IMPORTANT: The persist middleware rehydrates ASYNCHRONOUSLY.  Before
 * hydration finishes `current` is `null` even when localStorage has a
 * saved workspace.  We track `_hydrated` so the auto-select effect in
 * `useWorkspaces()` waits until rehydration is done before deciding
 * whether to fall back to the first workspace.
 */
export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set) => ({
      current: null,
      _hydrated: false,
      setCurrent: (w) => set({ current: w }),
    }),
    {
      name: 'iheart:workspace',
      partialize: (state) => ({ current: state.current }),
      onRehydrateStorage: () => {
        // Called when rehydration finishes (success or error)
        return (state) => {
          if (state) state._hydrated = true;
        };
      },
    },
  ),
);

/**
 * Fetches the user's workspaces and auto-selects from persisted state
 * or falls back to the first workspace if nothing is saved.
 *
 * Waits for Zustand persist rehydration to complete before running
 * the auto-select logic, preventing the race condition where the
 * query resolves before localStorage is read.
 */
export function useWorkspaces() {
  const setCurrent = useWorkspaceStore((s) => s.setCurrent);
  const current = useWorkspaceStore((s) => s.current);
  const hydrated = useWorkspaceStore((s) => s._hydrated);
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api<Workspace[]>('/me/workspaces'),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!hydrated) return;

    if (query.data && query.data.length > 0) {
      if (!current) {
        setCurrent(query.data[0]!);
      } else {
        const fresh = query.data.find((w) => w.publicId === current.publicId);
        if (fresh) {
          if (JSON.stringify(fresh) !== JSON.stringify(current)) {
            setCurrent(fresh);
          }
        } else if (!user?.isSuperAdmin) {
          setCurrent(query.data[0]!);
        }
      }
    }
  }, [hydrated, current, query.data, setCurrent, user?.isSuperAdmin]);

  return query;
}

export function useCurrentWorkspace(): Workspace | null {
  return useWorkspaceStore((s) => s.current);
}

export interface WorkspaceMember {
  publicId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: string;
  joinedAt: string;
}

/**
 * Create a new workspace. The current user becomes the OWNER.
 * On success, auto-selects the new workspace as current.
 */
export function useCreateWorkspace() {
  const qc = useQueryClient();
  const setCurrent = useWorkspaceStore((s) => s.setCurrent);

  return useMutation({
    mutationFn: (input: { name: string; defaultTz: string; brandColor?: string | null }) =>
      api<Workspace>('/me/workspaces', { method: 'POST', body: input }),
    onSuccess: (newWs) => {
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      setCurrent(newWs);
    },
  });
}

/**
 * Permanently delete a workspace (owner or super-admin). Irreversible — the
 * server wipes all of its content and cancels its billing. On success the
 * current selection is cleared and the app reloads to pick another workspace.
 */
export function useDeleteWorkspace() {
  const setCurrent = useWorkspaceStore((s) => s.setCurrent);
  return useMutation({
    // The delete endpoint lives on the workspace-settings controller:
    // DELETE /workspaces/:workspaceId/settings
    mutationFn: (publicId: string) => api(`/workspaces/${publicId}/settings`, { method: 'DELETE' }),
    onSuccess: () => {
      setCurrent(null);
      window.location.assign('/dashboard');
    },
  });
}

/**
 * Fetch workspace members — useful for assignee pickers, mentions, etc.
 */
export function useWorkspaceMembers() {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['workspace-members', ws?.publicId],
    queryFn: async () => {
      const res = await api<{ data: WorkspaceMember[] }>(`/workspaces/${ws!.publicId}/members`);
      return res.data;
    },
    enabled: !!ws,
    staleTime: 60_000,
  });
}
